// Live task pages (S97, docs/08 "Client edge"): a linked browser watches task groups and tasks
// over the relay socket it already holds; each `task.changed` / `task_group.changed` frame is a
// wake-up that makes the pages' queries stale, and they refetch what they show. Without a linked
// browser or an open socket the pages poll instead (`refetchInterval`).
import { type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect } from "react";

import { DeviceContext } from "../device/context";
import type { ClientFrame } from "../proto/frames";
import { bytesToUlid, ulidToBytes } from "../proto/ulid";
import { useRelayStatus } from "../relay/useRelayStatus";

/** How often a page that is not live refetches. */
export const POLL_MS = 10_000;

/** The query keys a change frame makes stale (react-query matches them as prefixes). */
export function staleKeys(frame: ClientFrame): QueryKey[] {
  if (frame.type === "task.changed") {
    const task = bytesToUlid(frame.task_id);
    const group = bytesToUlid(frame.task_group_id);
    return [["tasks", task], ["task-groups", group, "tasks"], ["costs", "group", group]];
  }
  if (frame.type === "task_group.changed") {
    const group = bytesToUlid(frame.task_group_id);
    // The group's page (its row, requirements, tasks), its costs, and the lists' rows.
    return [["task-groups", group], ["costs", "group", group], ["task-groups", "all"], ["task-groups", "own"]];
  }
  return [];
}

export interface LiveTarget {
  groups?: string[];
  tasks?: string[];
}

/**
 * Watch these groups and tasks while mounted. Returns whether updates arrive live; when not, the
 * caller polls (pass `refetchInterval(live)` to its queries).
 */
export function useLiveTasks({ groups = [], tasks = [] }: LiveTarget): boolean {
  const device = useContext(DeviceContext);
  const relay = device?.kind === "linked" ? device.relay : undefined;
  const status = useRelayStatus(relay);
  const queryClient = useQueryClient();
  // Joined, so a render naming the same ids keeps the subscription.
  const groupsKey = groups.filter(Boolean).join(",");
  const tasksKey = tasks.filter(Boolean).join(",");
  const watching = groupsKey !== "" || tasksKey !== "";

  useEffect(() => {
    if (!relay || !watching) return;
    const ids = (joined: string) => (joined ? joined.split(",").map(ulidToBytes) : []);
    const unsubscribe = relay.subscribe({ task_groups: ids(groupsKey), tasks: ids(tasksKey) });
    const off = relay.onFrame((frame) => {
      for (const queryKey of staleKeys(frame)) void queryClient.invalidateQueries({ queryKey });
    });
    return () => {
      off();
      unsubscribe();
    };
  }, [relay, groupsKey, tasksKey, watching, queryClient]);

  return watching && status === "open";
}

/** The refetch interval for a page's queries: none while live, else polling. */
export const refetchInterval = (live: boolean): number | false => (live ? false : POLL_MS);
